import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { NewsCategory, NewsDetail, NewsListItem, NewsTableResponse } from '../../../../core/models/cityparking/news.model';
import { ApiNewsService } from '../../../../core/services/api/cityparking/api-news.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalNewsComponent } from '../../modals/cityparking/modal-news/modal-news.component';

@Component({
  selector: 'app-news-management',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalNewsComponent],
  templateUrl: './news-management.component.html',
  styleUrl: './news-management.component.css',
})
export class NewsManagementComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiNewsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  /** board = อ่านข่าว · manage = ตาราง CRUD */
  viewMode = signal<'board' | 'manage'>('board');

  items = signal<NewsListItem[]>([]);
  categories = signal<NewsCategory[]>([]);
  isLoading = signal(false);
  totalCount = signal(0);
  page = signal(1);
  pageSize = signal(12);
  pageSizeOptions: number[] = [12, 24, 48];
  managePageSizeOptions: number[] = [10, 20, 50, 100];
  lastFetchedAt = signal<Date | null>(null);

  /** '' = ทั้งหมด · public · internal */
  filterVisibility = '';
  filterStatus = '';
  filterCategoryId: number | null = null;
  searchText = '';
  filterByCurrentSite = false;

  isModalOpen = signal(false);
  editId = signal<number | null>(null);

  isDetailOpen = signal(false);
  isDetailLoading = signal(false);
  detail = signal<NewsDetail | null>(null);
  zoomUrl = signal<string | null>(null);

  constructor() {
    effect(() => {
      this.siteState.selectedSite();
      if (this.filterByCurrentSite) {
        untracked(() => this.reload());
      }
    });
  }

  get currentPageSizeOptions(): number[] {
    return this.viewMode() === 'board' ? this.pageSizeOptions : this.managePageSizeOptions;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount() / this.pageSize()));
  }

  get startItemIndex(): number {
    if (this.totalCount() === 0) return 0;
    return (this.page() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.page() * this.pageSize(), this.totalCount());
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.page() - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  ngOnInit(): void {
    this.api.getNewsCategories(true).subscribe({
      next: (cats) => this.categories.set(cats ?? []),
    });
    this.reload();
  }

  setViewMode(mode: 'board' | 'manage'): void {
    if (this.viewMode() === mode) return;
    this.viewMode.set(mode);
    this.page.set(1);
    this.pageSize.set(mode === 'board' ? 12 : 20);
    this.filterVisibility = mode === 'board' ? '' : this.filterVisibility;
    if (mode === 'board') {
      this.filterStatus = '';
    }
    this.reload();
  }

  onRefresh(): void {
    this.reload();
  }

  onSearch(): void {
    this.page.set(1);
    this.reload();
  }

  onPageChange(next: number): void {
    if (next < 1 || next > this.totalPages) return;
    this.page.set(next);
    this.reload();
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.page.set(1);
    this.reload();
  }

  openCreate(): void {
    this.editId.set(null);
    this.isModalOpen.set(true);
  }

  openEdit(item: NewsListItem | NewsDetail): void {
    this.editId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.editId.set(null);
  }

  onSaved(): void {
    this.reload();
    if (this.isDetailOpen() && this.detail()?.id) {
      this.openDetail(this.detail()!.id);
    }
  }

  openDetail(id: number): void {
    this.isDetailOpen.set(true);
    this.isDetailLoading.set(true);
    this.detail.set(null);
    this.zoomUrl.set(null);

    const siteId = this.filterByCurrentSite
      ? this.siteState.selectedSite()?.id
      : undefined;

    this.api.getStaffNewsById(id, siteId && siteId > 0 ? siteId : undefined).subscribe({
      next: (res) => {
        this.detail.set(this.normalizeDetail(res));
        this.isDetailLoading.set(false);
      },
      error: () => {
        // บอร์ดไม่เจอ (หมดอายุ/draft) — fallback หน้าจัดการ
        this.api.getNewsById(id).subscribe({
          next: (res) => {
            this.detail.set(this.normalizeDetail(res));
            this.isDetailLoading.set(false);
          },
          error: (err) => {
            this.isDetailLoading.set(false);
            this.isDetailOpen.set(false);
            this.toast.error(err?.error?.message || 'โหลดรายละเอียดไม่สำเร็จ');
          },
        });
      },
    });
  }

  closeDetail(): void {
    this.isDetailOpen.set(false);
    this.detail.set(null);
    this.zoomUrl.set(null);
  }

  openZoom(url: string | null | undefined): void {
    if (!url) return;
    this.zoomUrl.set(url);
  }

  closeZoom(): void {
    this.zoomUrl.set(null);
  }

  openLink(url: string | null | undefined): void {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  }

  publishLabel(item: NewsListItem | NewsDetail): string {
    const published = item.publishedDate || item.publishFrom;
    if (published) return this.formatDate(published);
    return '—';
  }

  statusClass(status: string): string {
    switch (status) {
      case 'published':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200/60';
      case 'draft':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200/60';
      case 'archived':
        return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border border-slate-200/60';
      default:
        return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
    }
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'published':
        return 'เผยแพร่';
      case 'draft':
        return 'ร่าง';
      case 'archived':
        return 'เก็บถาวร';
      default:
        return status || '—';
    }
  }

  visibilityClass(visibility: string): string {
    switch (visibility) {
      case 'public':
        return 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 border border-sky-200/60';
      case 'internal':
        return 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border border-violet-200/60';
      default:
        return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
    }
  }

  visibilityLabel(visibility: string): string {
    switch (visibility) {
      case 'public':
        return 'สาธารณะ';
      case 'internal':
        return 'ภายใน';
      default:
        return visibility || '—';
    }
  }

  private reload(): void {
    if (this.viewMode() === 'board') {
      this.loadBoard();
    } else {
      this.loadManageTable();
    }
  }

  private loadBoard(): void {
    this.isLoading.set(true);
    const siteId = this.filterByCurrentSite
      ? this.siteState.selectedSite()?.id
      : undefined;

    this.api
      .getStaffNewsForBoard({
        page: this.page(),
        pageSize: this.pageSize(),
        visibility: this.filterVisibility || undefined,
        categoryId: this.filterCategoryId || undefined,
        search: this.searchText.trim() || undefined,
        siteId: siteId && siteId > 0 ? siteId : undefined,
      })
      .subscribe({
        next: (res) => {
          const raw = res as NewsTableResponse & { Items?: NewsListItem[]; TotalCount?: number };
          this.items.set(raw?.items ?? raw?.Items ?? []);
          this.totalCount.set(raw?.totalCount ?? raw?.TotalCount ?? 0);
          this.lastFetchedAt.set(new Date());
          this.isLoading.set(false);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.toast.error(err?.error?.message || 'โหลดบอร์ดข่าวไม่สำเร็จ');
        },
      });
  }

  private loadManageTable(): void {
    this.isLoading.set(true);
    const siteId = this.filterByCurrentSite
      ? this.siteState.selectedSite()?.id
      : undefined;

    this.api
      .getNews({
        page: this.page(),
        pageSize: this.pageSize(),
        visibility: this.filterVisibility || undefined,
        status: this.filterStatus || undefined,
        categoryId: this.filterCategoryId || undefined,
        search: this.searchText.trim() || undefined,
        siteId: siteId && siteId > 0 ? siteId : undefined,
      })
      .subscribe({
        next: (res) => {
          const raw = res as NewsTableResponse & { Items?: NewsListItem[]; TotalCount?: number };
          this.items.set(raw?.items ?? raw?.Items ?? []);
          this.totalCount.set(raw?.totalCount ?? raw?.TotalCount ?? 0);
          this.lastFetchedAt.set(new Date());
          this.isLoading.set(false);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.toast.error(err?.error?.message || 'โหลดข่าวไม่สำเร็จ');
        },
      });
  }

  private normalizeDetail(res: NewsDetail): NewsDetail {
    const raw = res as NewsDetail & Record<string, unknown>;
    return {
      ...res,
      id: Number(res.id ?? raw['Id'] ?? 0),
      title: String(res.title ?? raw['Title'] ?? ''),
      summary: (res.summary ?? raw['Summary'] ?? null) as string | null,
      body: (res.body ?? raw['Body'] ?? null) as string | null,
      coverImageUrl: (res.coverImageUrl ?? raw['CoverImageUrl'] ?? null) as string | null,
      categoryName: (res.categoryName ?? raw['CategoryName'] ?? null) as string | null,
      visibility: String(res.visibility ?? raw['Visibility'] ?? ''),
      status: String(res.status ?? raw['Status'] ?? ''),
      siteId: (res.siteId ?? raw['SiteId'] ?? null) as number | null,
      siteName: (res.siteName ?? raw['SiteName'] ?? null) as string | null,
      publishFrom: (res.publishFrom ?? raw['PublishFrom'] ?? null) as string | null,
      publishTo: (res.publishTo ?? raw['PublishTo'] ?? null) as string | null,
      publishedDate: (res.publishedDate ?? raw['PublishedDate'] ?? null) as string | null,
      pin: !!(res.pin ?? raw['Pin']),
      linkUrl: (res.linkUrl ?? raw['LinkUrl'] ?? null) as string | null,
      linkLabel: (res.linkLabel ?? raw['LinkLabel'] ?? null) as string | null,
      images: (res.images ?? raw['Images'] ?? []) as NewsDetail['images'],
    };
  }

  siteNewsScopeLabel(item: NewsListItem): string {
    return item.showOnGlobal === false ? 'เฉพาะหน้าลาน' : 'Home + หน้าลาน';
  }

  siteNewsScopeClass(item: NewsListItem): string {
    return item.showOnGlobal === false
      ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
      : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
  }
}
